<template>
  <div class="faq-page">
    <!-- ---------------------------------------------------------------
         Hero
    ---------------------------------------------------------------- -->
    <section class="faq-hero">
      <div class="faq-hero-text">
        <span class="faq-eyebrow">FAQs</span>
        <h1>Ride. Smart. <br />Ask anything.</h1>
        <p class="faq-hero-lede">
          Quick answers about your cover, locks, travel, claims and more. Can't
          find what you need? Call 0800 888 6745 (Monday to Friday, 9am–5pm) or
          <nuxt-link no-prefetch to="/contact-us">get in touch</nuxt-link>.
        </p>

        <form class="faq-search" @submit.prevent="search">
          <svg
            class="faq-search-icon"
            viewBox="0 0 24 24"
            aria-hidden="true"
            focusable="false"
          >
            <circle
              cx="11"
              cy="11"
              r="7"
              fill="none"
              stroke="currentColor"
              stroke-width="1.7"
            />
            <path
              d="M20.5 20.5L16 16"
              fill="none"
              stroke="currentColor"
              stroke-width="1.7"
              stroke-linecap="round"
            />
          </svg>
          <input
            id="faq-search"
            v-model="searchPhase"
            type="text"
            class="faq-search-input"
            placeholder="Search, e.g. “lock”, “e-bike” or “excess”"
          />
          <button type="submit" class="button ps-button call-to-action">
            Search
          </button>
        </form>
      </div>

      <div class="faq-hero-illustration">
        <img
          src="~/assets/images/illustrations/Policy-page.svg"
          alt="Illustrated cyclists riding past a policy information sign"
        />
      </div>
    </section>

    <!-- ---------------------------------------------------------------
         Quick links — one pill per real category, so they always match
         what's actually on the page (the old hrefs were hand-typed and
         had drifted from the category ids).
    ---------------------------------------------------------------- -->
    <nav class="faq-quicklinks" aria-label="Jump to a topic">
      <a
        v-for="cat in faqList"
        :key="cat.id"
        :href="`#${categorySlug(cat.id)}`"
        class="ps-pill faq-quicklink"
      >
        <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
          <path
            v-for="(d, i) in categoryIconPaths(cat.id)"
            :key="i"
            :d="d"
            fill="none"
            stroke="currentColor"
            stroke-width="1.7"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
        {{ cat.title }}
      </a>
    </nav>

    <!-- ---------------------------------------------------------------
         FAQ categories
    ---------------------------------------------------------------- -->
    <div class="faq-body">
      <template v-for="item in faqList">
        <section
          :id="categorySlug(item.id)"
          :key="item.id"
          class="faq-category"
        >
          <h2 class="faq-category-title">
            <span class="faq-category-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" focusable="false">
                <path
                  v-for="(d, i) in categoryIconPaths(item.id)"
                  :key="i"
                  :d="d"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.7"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </span>
            {{ item.title }}
          </h2>

          <div class="ps-faq">
            <details
              v-for="faq in item.items"
              :id="faq.anchor"
              :key="faq.title"
              :name="faqGroupName(item.id)"
              class="ps-faq-item"
              @toggle="revealQuarantined(faq)"
            >
              <summary>{{ faq.title }}</summary>
              <div
                v-if="!faq.quarantined || openedQuarantined[faq.title]"
                class="ps-faq-answer"
                v-html="faq.value"
              ></div>
            </details>
          </div>
        </section>
      </template>
    </div>

    <!-- ---------------------------------------------------------------
         Still stuck? — a soft landing rather than a dead end.
    ---------------------------------------------------------------- -->
    <section class="faq-cta">
      <h2>Still have a question?</h2>
      <p>
        If you couldn't find your answer above, get in touch and we'll sort it
        out — or just get a price and see for yourself.
      </p>
      <div class="faq-cta-actions">
        <nuxt-link
          no-prefetch
          to="/quote?src=faq-footer"
          class="button ps-button call-to-action"
        >
          Get a quote →
        </nuxt-link>
        <nuxt-link
          no-prefetch
          to="/contact-us"
          class="button ps-button is-secondary"
        >
          Contact us
        </nuxt-link>
      </div>
    </section>

    <p class="faq-meta">
      These FAQs summarise our cover. The full terms are in the
      <nuxt-link no-prefetch to="/policy-information"
        >Policy Document</nuxt-link
      >
      and your Policy Schedule, which take priority. Last updated for policy
      wording 11/26.
    </p>
  </div>
</template>

<script>
import { FAQ_DATA } from '@/helpers'
import { buildSocialMeta } from '@/helpers/social-meta'
import { getCanonicalUrl } from '@/helpers/seo'
import { jsonLdScript, sanitizeFaqAnswerForSchema } from '@/helpers/blog-schema'

const CANONICAL_PATH = '/faq'

// Simple line icons, one per category, matching the stroke style used
// sitewide (24x24 viewBox, stroke-width 1.7, currentColor). Kept local to
// this page rather than in FAQ_DATA — purely presentational, no reason for
// the compliance-tracked data file to carry it.
const CATEGORY_ICONS = {
  'About-Pedalsure-Cycling-Insurance': [
    'M12 3.8a8.2 8.2 0 1 1 0 16.4 8.2 8.2 0 0 1 0-16.4z',
    'M9.6 9.4a2.5 2.5 0 1 1 3.3 2.4c-.6.2-.9.7-.9 1.3v.5',
    'M12 16.6h.01',
  ],
  ManagingyourpolicywithPedalsure: [
    'M6 3.5h8L18.5 8v12a.5.5 0 0 1-.5.5H6a.5.5 0 0 1-.5-.5V4a.5.5 0 0 1 .5-.5z',
    'M13.5 3.7V8.5h4.8',
    'M8.5 13h7M8.5 16.5h4.5',
  ],
  'Locks and security': [
    'M5.5 10.5h13a1 1 0 0 1 1 1v7a1 1 0 0 1-1 1h-13a1 1 0 0 1-1-1v-7a1 1 0 0 1 1-1z',
    'M8 10.5V7.5a4 4 0 0 1 8 0v3',
  ],
  Travel: [
    'M20.5 3.5L10.5 13.5',
    'M20.5 3.5L14 20.5L10.5 13.5L3.5 9.5L20.5 3.5Z',
  ],
  Activities: [
    'M7 4h10v4a5 5 0 0 1-10 0V4z',
    'M7 5H4.5a2 2 0 0 0 0 4H7',
    'M17 5h2.5a2 2 0 0 1 0 4H17',
    'M12 13v4',
    'M9 20h6',
  ],
  'Bike types, parts and accessories': [
    'M14.7 6.3a4 4 0 1 1-5.4 5.4L4 17l3 3 5.3-5.3a4 4 0 0 0 5.4-5.4l-2.6 2.6-2.7-2.7 2.6-2.6z',
  ],
  Claims: [
    'M12 3.2l7 2.8v6c0 4.2-2.9 7.6-7 9-4.1-1.4-7-4.8-7-9V6l7-2.8z',
    'M9 12l2.2 2.2L15.2 10',
  ],
}

export default {
  name: 'FaqPage',
  data() {
    return {
      searchPhase: '',
      faqList: FAQ_DATA,
      // Answers flagged `quarantined` (PS-54/55/56/60 — live compliance
      // items) stay out of the server-rendered HTML and out of the FAQPage
      // schema. Their text is only added to the DOM once a visitor actually
      // opens that row, matching how the old JS-only accordion behaved.
      openedQuarantined: {},
    }
  },
  head() {
    const title = 'Cycling insurance FAQs | Pedalsure'
    const description =
      'Answers to common questions about Pedalsure cycling insurance: Theft Only and Theft and Accidental Damage cover, locks and security, travel, e-bikes, claims and more.'
    const keywords = 'bicycle insurance cover'
    return {
      title,
      meta: [
        { hid: 'description', name: 'description', content: description },
        { hid: 'keywords', name: 'keywords', content: keywords },
        ...buildSocialMeta({
          title,
          description,
          path: CANONICAL_PATH,
        }).meta,
      ],
      script: [jsonLdScript('ld-faq', this.faqSchema)],
    }
  },
  computed: {
    // Same array the template renders, filtered to non-quarantined items —
    // schema and visible text can't drift because they read the same source.
    faqSchema() {
      const canonicalUrl = getCanonicalUrl(CANONICAL_PATH)
      const eligible = FAQ_DATA.flatMap((category) =>
        category.items.filter((item) => !item.quarantined)
      )
      return {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        '@id': `${canonicalUrl}#faq`,
        mainEntity: eligible.map((faq) => ({
          '@type': 'Question',
          name: faq.title,
          acceptedAnswer: {
            '@type': 'Answer',
            text: sanitizeFaqAnswerForSchema(faq.value),
          },
        })),
      }
    },
  },
  mounted() {
    // /faq?q=… (the contact page's search box) starts with those results.
    if (this.$route.query.q) {
      this.searchPhase = String(this.$route.query.q)
      this.search()
    }
    // Deep links like /faq#lock-requirements open the matching answer.
    this.openHashAnswer()
    window.addEventListener('hashchange', this.openHashAnswer)
  },
  beforeDestroy() {
    window.removeEventListener('hashchange', this.openHashAnswer)
  },
  methods: {
    // Case-insensitive, every word must appear somewhere in the question or
    // the answer's text (tags stripped, so "strong" doesn't match markup).
    search() {
      const words = this.searchPhase.toLowerCase().split(/\s+/).filter(Boolean)
      this.faqList = FAQ_DATA.map(({ items, ...rest }) => {
        const _items = items.filter((item) => {
          const text = `${item.title} ${item.value.replace(
            /<[^>]+>/g,
            ' '
          )}`.toLowerCase()
          return words.every((word) => text.includes(word))
        })
        return { ...rest, items: _items }
      }).filter(({ items, ...rest }) => items.length > 0)
    },
    openHashAnswer() {
      const id = decodeURIComponent(window.location.hash.slice(1))
      const el = id && document.getElementById(id)
      if (el && el.tagName === 'DETAILS') {
        el.open = true
        el.scrollIntoView()
      }
    },
    revealQuarantined(faq) {
      if (faq.quarantined) {
        this.$set(this.openedQuarantined, faq.title, true)
      }
    },
    // `<details name>` groups need an attribute-safe token; category ids
    // here can contain spaces/commas, so this can't just reuse item.id raw.
    faqGroupName(categoryId) {
      return `faq-group-${categoryId.replace(/[^a-zA-Z0-9-]+/g, '-')}`
    },
    // Same slugging for the section id the quick links jump to.
    categorySlug(categoryId) {
      return `faq-cat-${categoryId.replace(/[^a-zA-Z0-9-]+/g, '-')}`
    },
    categoryIconPaths(categoryId) {
      return CATEGORY_ICONS[categoryId] || []
    },
  },
}
</script>

<style lang="scss" scoped>
.faq-page {
  max-width: 1040px;
  margin: 0 auto;
  padding: 0 20px 72px;
}

/* ---- Hero --------------------------------------------------------- */
.faq-hero {
  display: flex;
  align-items: center;
  gap: 40px;
  padding: 56px 0 24px;
  @include mobile {
    flex-direction: column;
    padding-top: 32px;
    text-align: center;
  }
}

.faq-hero-text {
  flex: 1 1 480px;
  min-width: 0;
}

.faq-eyebrow {
  display: inline-flex;
  align-items: center;
  height: 28px;
  padding: 0 14px;
  border-radius: $ps-radius-pill;
  background-color: $ps-primary-50;
  color: $ps-primary;
  font-size: $ps-text-label;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  margin-bottom: 16px;
}

.faq-hero-text h1 {
  margin: 0 0 16px;
}

.faq-hero-lede {
  max-width: 46ch;
  color: $ps-body-color;
  @include mobile {
    max-width: none;
    margin-left: auto;
    margin-right: auto;
  }
}

.faq-hero-illustration {
  flex: 0 0 auto;
  width: 340px;
  max-width: 42%;
  @include mobile {
    width: 100%;
    max-width: 280px;
    margin-top: 8px;
  }
  img {
    width: 100%;
    height: auto;
    display: block;
  }
}

/* ---- Search --------------------------------------------------------- */
.faq-search {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 28px;
  padding: 6px 6px 6px 18px;
  background-color: $ps-white;
  border: 1.5px solid $ps-border-color;
  border-radius: $ps-radius-pill;
  box-shadow: 0 1px 2px rgba(20, 22, 31, 0.08);
  transition: border-color $ps-radius-sm;
  &:focus-within {
    border-color: $ps-primary;
  }
  @include mobile {
    flex-wrap: wrap;
    border-radius: $ps-radius-lg;
    padding: 14px;
  }
}

.faq-search-icon {
  flex: 0 0 auto;
  width: 20px;
  height: 20px;
  color: $ps-muted;
}

.faq-search-input {
  flex: 1;
  min-width: 0;
  border: none;
  outline: none;
  background: transparent;
  font-family: $family-regular;
  font-size: $ps-text-small;
  color: $ps-ink;
  height: 40px;
  &::placeholder {
    color: $ps-muted;
  }
  @include mobile {
    flex-basis: 100%;
    order: -1;
    margin-bottom: 4px;
  }
}

.faq-search .ps-button {
  flex: 0 0 auto;
  height: 44px;
  padding: 0 22px;
}

/* ---- Quick links ------------------------------------------------- */
.faq-quicklinks {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  padding: 24px 0 40px;
  border-bottom: 1px solid $ps-border-color;
}

.faq-quicklink {
  gap: 8px;
  text-decoration: none;
  svg {
    width: 18px;
    height: 18px;
    flex: 0 0 auto;
  }
}

/* ---- Category sections -------------------------------------------- */
.faq-body {
  padding-top: 8px;
}

.faq-category {
  padding: 40px 0;
  border-bottom: 1px solid $ps-border-color;
  &:last-child {
    border-bottom: none;
  }
  scroll-margin-top: 24px;
}

.faq-category-title {
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 0 0 4px;
}

.faq-category-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: 40px;
  height: 40px;
  border-radius: $ps-radius-md;
  background-color: $ps-primary-50;
  color: $ps-primary;
  svg {
    width: 20px;
    height: 20px;
  }
}

// Answers are v-html'd, so scoped styles need ::v-deep to reach them.
// Bulma's reset strips list bullets and paragraph spacing.
.ps-faq-answer ::v-deep {
  p,
  ul {
    margin: 0 0 12px;
  }
  ul {
    list-style: disc;
    padding-left: 22px;
  }
  ul ul {
    list-style: circle;
    margin: 4px 0 0;
  }
  li {
    margin: 4px 0;
  }
  > :last-child {
    margin-bottom: 0;
  }
}

/* ---- Closing CTA ---------------------------------------------------- */
.faq-cta {
  margin-top: 48px;
  padding: 48px 32px;
  border-radius: $ps-radius-lg;
  background-color: $ps-surface;
  text-align: center;
  h2 {
    margin: 0 0 8px;
  }
  p {
    max-width: 48ch;
    margin: 0 auto 24px;
    color: $ps-body-color;
  }
}

.faq-meta {
  margin: 24px 0 0;
  font-size: $ps-text-label;
  color: $ps-muted;
  text-align: center;
}

.faq-cta-actions {
  display: flex;
  justify-content: center;
  gap: 16px;
  flex-wrap: wrap;
}
</style>
